<template>
  <combobox
    v-slot="{ open, value }"
    v-model="localModel"
    v-bind="{ multiple }"
    as="div"
    class="flex flex-col gap-1"
  >
    <combobox-label v-if="label" class="block leading-none">
      {{ label }}
    </combobox-label>

    <p v-if="help || $slots.help" :id="aria['aria-describedby']" class="help text-sm text-gray-500">
      <slot v-if="$slots.help" name="help"/>
      <template v-else>
        {{ help }}
      </template>
    </p>

    <div v-if="multiple && value.length" class="flex flex-wrap gap-2 mb-2">
      <l-button
        v-for="item in value"
        :key="item[itemKey]"
        :aria-label="`Remove ${item.name}`"
        :class="classes?.tag ?? [
          'flex items-center rounded-full  whitespace-nowrap',
          'bg-white dark:bg-gray-800/50 border-2 border-gray-200 hover:bg-gray-200 text-blue dark:text-white',
          size === 'md' && 'gap-1.5 pl-3 pr-2 py-0.5 text-sm sm:text-base',
          size === 'lg' && 'gap-2 pl-4 pr-3 py-1 text-base sm:text-lg',
        ]"
        variant="link"
        @click="() => removeItem(item)"
      >
        {{ item.name }}
        <icon name="remove" class="text-xs text-gray-500 sm:text-sm"/>
      </l-button>
    </div>

    <div class="relative field">
      <div class="bg-white dark:bg-gray-800/50 text-left shadow-sm block rounded-md">
        <combobox-input
          ref="input"
          :class="[
            'w-full border border-gray-300 dark:border-gray-800/50 leading-5 text-blue dark:text-white rounded-md placeholder:text-gray-500',
            size === 'sm' && 'text-base py-2 px-3',
            size === 'md' && 'text-base py-2.5 px-3',
            size === 'lg' && 'text-xl py-3 px-4',
            loading && 'disabled:bg-white dark:bg-gray-800',
            disabled && 'disabled:opacity-50',
          ]"
          autocomplete="off"
          :disabled="disabled"
          :display-value="item => multiple ? null : item?.name ?? localModel"
          v-bind="{ autofocus, id, placeholder, required, ...aria }"
          @focus="() => autofocus || open ? null : button?.$el.click()"
          @input.stop="query = $event.target.value"
        />

        <combobox-button
          v-if="chevron || loading"
          ref="button"
          :class="[
            'absolute inset-y-0 right-0 flex items-center px-3 rounded-r-md',
            'border border-transparent bg-clip-padding',
            'text-gray-400 disabled:text-gray-400',
            size === 'sm' && 'px-2',
            size === 'md' && 'px-3',
            size === 'lg' && 'px-4',
            !disabled && 'hover:text-gray-500',
          ]"
          :disabled="disabled"
          @mousedown.prevent
        >
          <icon v-if="loading" name="loading"/>
          <icon
            v-else
            name="chevronDown"
            :size="{ md: 'sm', lg: 'md' }[size]"
            :class="['transition-transform', { 'rotate-180': open && !disabled }]"
          />
        </combobox-button>
      </div>

      <transition-root
        v-if="!disabled"
        leave="transition ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        @after-leave="query = ''"
      >
        <combobox-options
          :as="links ? 'div' : 'ul'"
          :class="[
            'bg-white dark:bg-gray-900 absolute w-full max-h-60 mt-1 pl-0 rounded-md shadow-lg z-30',
            'ring-1 ring-black/10 dark:ring-white/10',
            'overflow-y-auto overflow-x-hidden',
            'text-left list-none',
            size === 'sm' && 'text-base',
            size === 'md' && 'text-base',
            size === 'lg' && 'text-lg',
          ]"
        >
          <combobox-option v-if="allowCustom" class="hidden" :value="query">
            {{ query }}
          </combobox-option>

          <component
            :is="links ? 'div' : 'li'"
            v-else-if="query.length && !items.length"
            :class="[
              'relative block select-none',
              size === 'sm' && 'py-1.5 px-2 gap-2',
              size === 'md' && 'py-2 px-4 gap-3',
              size === 'lg' && 'py-2 px-4 gap-3',
            ]"
          >
            Nothing found
          </component>

          <combobox-option
            v-for="item in items"
            :key="item[itemKey]"
            :as="links ? 'a' : 'li'"
            :href="links ? item.url : null"
            :class="[
              'relative w-full select-none flex items-center my-px',
              size === 'sm' && 'py-1.5 px-2 gap-2',
              size === 'md' && 'py-2 px-4 gap-3',
              size === 'lg' && 'py-2 px-4 gap-3',
              'text-inherit hover:text-inherit',
              'ui-active:bg-gray-400/10',
              'ui-selected:font-medium',
            ]"
            :value="item"
            @click.prevent
          >
            <icon
              v-if="item.icon"
              :name="item.icon"
              color="gray"
              set="regular"
            />
            <div class="overflow-hidden flex-1 flex gap-3 justify-between items-baseline">
              <slot name="prepend-item" v-bind="{ item }"/>
              <span class="truncate">
                {{ item.name }}
              </span>
              <slot name="append-item" v-bind="{ item }"/>
            </div>
          </combobox-option>
        </combobox-options>
      </transition-root>
    </div>

    <slot name="errorMessage"/>

    <slot v-if="$slots.default"/>
    <template v-if="name && multiple">
      <input
        v-for="item in value"
        :key="item[itemKey]"
        type="hidden"
        :value="item[itemKey]"
        v-bind="{ name }"
      >
    </template>
    <input
      v-else-if="name"
      type="hidden"
      :value="value[itemKey]"
      v-bind="{ name }"
    >
  </combobox>
</template>

<script setup>
import Icon from "@/components/Icon.vue"
import LButton from "@/components/buttons/LButton.vue"
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
  TransitionRoot,
} from "@headlessui/vue"
import { computed, ref, useSlots, watch } from "vue"
import { pick, uniqueId } from "lodash-es"

const model = defineModel([Array, Object, String])

const props = defineProps({
  id: { type: String, default: () => `input-${uniqueId()}` },
  items: { type: Array, required: true },
  placeholder: { type: String, default: "Enter an item" },
  size: { type: String, default: "md" },
  name: String,
  label: String,
  help: String,
  autofocus: Boolean,
  disabled: Boolean,
  loading: Boolean,
  multiple: Boolean,
  required: Boolean,
  allowCustom: Boolean,
  links: Boolean,
  max: Number,
  itemKey: { type: String, default: "id" },
  searchFields: { type: Array, default: () => ["name", "code", "slug", "indicators"] },
  classes: Object,
  errorMessage: String,
})

const emit = defineEmits(["input", "update:query"])

const slots = useSlots()

const localModel = ref(model.value)

const query = ref("")

const button = ref()

const input = ref()

const chevron = computed(() => !props.allowCustom)

const disabled = computed(() => props.disabled || props.loading || (props.max && model.value.length >= props.max))

const aria = computed(() => {
  const attrs = {}

  if (props.help || slots.help) {
    attrs["aria-describedby"] = `${props.id}_help`
  }

  return attrs
})

const items = computed(() => {
  let items = searchItems.value

  if (query.value !== "") {
    items = items.filter(item => item.searchText.includes(searchQuery.value))
  }

  // Hiding selected values because there's a bug in headless-ui where it doesn't
  // mark selected values as selected, then you can select them twice and have duplicates.
  // This only happens for items that are selected by default (initial values) and when
  // multiple is true. Seems like multi select is buggy.
  // Related: https://github.com/tailwindlabs/headlessui/issues/2434
  if (props.multiple) {
    const selectedIds = localModel.value.map((v) => v[props.itemKey])

    items = items.filter(item => !selectedIds.includes(item[props.itemKey]))
  }

  return items
})

const searchItems = computed(() => {
  return props.items.map(item => ({
    ...item,
    searchText: (
      (item.searchText ?? Object.values(pick(item, props.searchFields)).join(""))
        .replace(/\s+/g, "")
        .toLowerCase()
    ),
  }))
})

const searchQuery = computed(() => query.value.replace(/\s+/g, "").toLowerCase())

const removeItem = (subject) => {
  localModel.value = localModel.value.filter(item => item[props.itemKey] != subject[props.itemKey])
}

watch(localModel, value => {
  model.value = value

  emit("input", value)

  query.value = ""

  input.value.$el.value = query.value
})

watch(query, value => {
  emit("update:query", value)
})
</script>
